import { useState } from 'react';
import { IconArrowBackUp, IconBell, IconChecklist, IconChevronLeft, IconDots, IconDownload, IconFileUpload, IconPlus, IconSearch, IconSend, IconSignature, IconCalendar, IconUsersGroup } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader, useShell } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { InboxPane } from '../booking/InboxPane';
import type { BookingRequest } from '../booking/types';
import { DEFAULT_MESSAGE_ID, MAIL_TYPE, findMail, slaText } from './mailViewData';
import './msgI18n';
import s from './msg.module.css';

// Mint-Booking-Msg (1536): Mint-Booking mở thư info@ theo Message-ID (prop messageId). Chỉ đọc; nút ghi báo toast.
// Chưa làm: xem trước tệp, menu thao tác thư, Nhắn PIC / Duyệt gợi ý PIC (người khác PIC).
const ME = 'PHUONGHT';
const LABEL_COLOR: Record<string, string> = { BOOKING: 'var(--d-blue)', LICH_TAU: 'var(--d-amber)', NOTICE: 'var(--d-violet)', SI_VGM: 'var(--d-green)', URGENT_CASE: 'var(--badge)', CHUNG_TU: 'var(--d-blue)' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function MailView() {
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const { layout } = useShell();
  const params = useParams({ strict: false }) as { messageId?: string };
  const messageId = params.messageId ? decodeURIComponent(params.messageId) : DEFAULT_MESSAGE_ID;
  const { data } = useList('booking', { limit: 5000 });
  const [inbox, setInbox] = useState(true);
  const desktop = layout === 'side' || layout === 'rail';
  const noWrite = () => toast({ msg: t('bmsg.noWrite') });
  const back = () => void navigate({ to: '/ezy/booking' });
  const forced = readState();
  const v = findMail(messageId);
  const state: ScreenState = forced ?? (v ? 'ready' : 'empty');
  const m = v?.mail;
  const overdue = !!m && m.slaMin < 0;
  const ty = v ? MAIL_TYPE[v.type]! : null;
  const color = m ? (LABEL_COLOR[m.label] ?? 'var(--d-gray)') : '';

  const viewTabs = (
    <div className={s.hdrTabs}>
      <Segmented kind="toggle" label={t('bmsg.viewLabel')} value="req" onChange={back} items={[{ key: 'req', label: t('bmsg.tabReq') }, { key: 'vessel', label: t('bmsg.tabVessel') }]} />
      <div className={s.coord} role="group" aria-label={t('bmsg.coordGroup')}>
        <span className={s.av} aria-hidden="true">PH</span>
        <span className={s.coordTx}>{t('bmsg.coord')} <b>{ME}</b></span>
        <Button size="sm" variant="ghost" icon={<IconCalendar size={14} aria-hidden="true" />} title={t('bmsg.scheduleTip')} onClick={noWrite}>{t('bmsg.schedule')}</Button>
        <Button size="sm" variant="ghost" icon={<IconUsersGroup size={14} aria-hidden="true" />} title={t('bmsg.coordGroup')} onClick={noWrite}>{t('bmsg.coord')}</Button>
      </div>
    </div>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={t('bmsg.title')}
        tabs={desktop ? viewTabs : undefined}
        actions={
          desktop ? (
            <>
              <HeaderTool label={t('bmsg.load')} icon={IconDownload} onClick={noWrite} />
              <HeaderTool label={t('bmsg.tasks')} icon={IconChecklist} n={5} />
              <HeaderTool label={t('bmsg.notif')} icon={IconBell} n={2} />
              <HeaderTool label={t('bmsg.more')} icon={IconDots} onClick={noWrite} />
              <Button variant="secondary" icon={<IconSend size={16} aria-hidden="true" />} title={t('bmsg.bulkRodTip')} onClick={noWrite}>{t('bmsg.bulkRod')}</Button>
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('bmsg.newReq')}</Button>
            </>
          ) : undefined
        }
      />
      <div className={`${s.body} ${desktop ? '' : s.bodyOne} ${inbox ? '' : s.bodyRail}`}>
        {desktop && <InboxPane me={ME} requests={(data?.items ?? []) as BookingRequest[]} open={inbox} onToggle={() => setInbox((x) => !x)} onJob={back} onNew={noWrite} onWrite={noWrite} />}
        <StateView
          state={state}
          title={state === 'empty' ? t('bmsg.notFound') : undefined}
          desc={state === 'empty' ? t('bmsg.notFoundDesc') : undefined}
          icon={state === 'empty' ? <IconSearch size={26} stroke={1.8} /> : undefined}
          action={state === 'empty' ? <Button onClick={back}>{t('bmsg.backList')}</Button> : undefined}
          skeleton={<Skeleton rows={6} />}
        >
          {v && m && ty && (
            <section aria-label={t('bmsg.mailLabel')} className={s.mail}>
              <div className={s.top}>
                <button type="button" className={s.sq} aria-label={t('bmsg.backList')} title={t('bmsg.backList')} onClick={back}>
                  <IconChevronLeft size={16} aria-hidden="true" />
                </button>
                <span className={s.chip} style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color: 'var(--tx)' }}>{m.label}</span>
                <span className={s.chip} title={ty[2]}>{t('bmsg.type', { t: v.type, name: ty[0], due: ty[1] })}</span>
                {m.slaMin !== 0 && m.kind !== 'none' && <span className={`${s.chip} ${overdue ? s.chipErr : m.slaMin <= 15 ? s.chipSla : ''}`}>{slaText(m)}</span>}
                <span className={s.time}>{m.time}</span>
                <button type="button" className={s.sq} aria-haspopup="menu" aria-label={t('bmsg.mailOps')} onClick={noWrite}>⋯</button>
              </div>
              <div>
                <h2 className={s.subj}>{m.subj}</h2>
                <div className={s.from}>{m.from} → info@</div>
                <div className={s.mid}>
                  Message-ID {v.msgId}
                  <span className={s.from2}>{t('bmsg.openedFrom')}</span>
                </div>
              </div>
              {v.atts.length > 0 && (
                <div role="group" aria-label={t('bmsg.attLabel')} className={s.attRow}>
                  {v.atts.map((a) => (
                    <div key={a.name} className={s.att}>
                      <button type="button" aria-label={t('bmsg.view', { name: a.name })} title={`${a.name} · ${a.size}`} onClick={noWrite}>
                        <span className={s.ext} aria-hidden="true">{a.ext}</span>
                        <span className={s.nm}>{a.name}</span>
                        <span className={s.sz}>{a.size}</span>
                      </button>
                      <button type="button" aria-label={t('bmsg.attOps', { name: a.name })} onClick={noWrite}>
                        <IconDots size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  <span className={s.cnt}>{t('bmsg.files', { n: v.atts.length })}</span>
                  <button type="button" className={s.btn} onClick={noWrite}><IconDownload size={14} aria-hidden="true" />{t('bmsg.dlAll')}</button>
                  <button type="button" className={`${s.btn} ${s.btnPri}`} onClick={noWrite}><IconFileUpload size={14} aria-hidden="true" />{t('bmsg.upload')}</button>
                  <button type="button" className={s.btn} onClick={noWrite}><IconSignature size={14} aria-hidden="true" />{t('bmsg.stamp')}</button>
                </div>
              )}
              <div className={s.text}>{v.body}</div>
              <div className={s.pic}>
                <span className={s.picK}>PIC</span>
                {m.pic ? (
                  <>
                    <span className={s.av} aria-hidden="true">{m.pic.slice(0, 2)}</span>
                    <span>{m.pic}</span>
                    {v.why && <span className={s.why}>{v.why}</span>}
                  </>
                ) : (
                  <span className={s.why}>PIC:UNASSIGNED</span>
                )}
              </div>
              <div className={s.acts}>
                <button type="button" className={s.reply} onClick={noWrite}><IconArrowBackUp size={16} aria-hidden="true" />{t('bmsg.reply')}</button>
                <button type="button" className={s.more} aria-haspopup="menu" aria-label={t('bmsg.moreMail')} title={t('bmsg.more')} onClick={noWrite}>
                  <IconDots size={18} aria-hidden="true" />
                </button>
              </div>
            </section>
          )}
        </StateView>
      </div>
    </Page>
  );
}
